import EmailOutlined from '@mui/icons-material/EmailOutlined';
import ShareOutlined from '@mui/icons-material/ShareOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Box, Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import strings from 'common/Translation/Translate';
import DrawerMenu from 'components/DrawerMenu';
import { FC, useState } from 'react';

interface PreviewContractProps {
  pdfUrl: string;
  handleClose: any;
  openPdfDrawer: string | boolean;
  downloadContract?: any;
  loading: boolean;
  headerText: string;
  downloadButtonText?: string;
  prepareShareFile?: (type: string) => Promise<{ blob: Blob; fileName: string }>;
  shareEmail?: string;
}
const PreviewContract: FC<PreviewContractProps> = ({
  pdfUrl,
  handleClose,
  openPdfDrawer,
  downloadContract,
  headerText,
  downloadButtonText,
  prepareShareFile,
  shareEmail,
}) => {
  const [shareAnchor, setShareAnchor] = useState<HTMLElement | null>(null);
  const headerIconButtonSx = {
    flexShrink: 0,
    color: 'primary.main',
    border: '1px solid',
    borderColor: 'primary.main',
    borderRadius: 1,
    p: 0.4,
    bgcolor: 'background.paper',
    '&:hover': { bgcolor: 'primary.light', borderColor: 'primary.dark', color: 'primary.dark' },
  } as const;

  const handleShare = async (channel: 'email' | 'whatsapp') => {
    setShareAnchor(null);
    if (!prepareShareFile || openPdfDrawer === false) return;

    try {
      const { blob, fileName } = await prepareShareFile(String(openPdfDrawer));
      const safeName = fileName.toLowerCase().endsWith('.pdf') ? fileName : `${fileName}.pdf`;
      const file = new File([blob], safeName, { type: 'application/pdf' });
      const note = `${headerText}. ${strings.bookings_button_share_attach_hint}`;

      if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: headerText, text: headerText });
        return;
      }

      const objectUrl = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = safeName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      if (channel === 'email') {
        const to = shareEmail?.trim() ? encodeURIComponent(shareEmail.trim()) : '';
        window.open(
          `mailto:${to}?subject=${encodeURIComponent(headerText)}&body=${encodeURIComponent(note)}`,
          '_blank',
        );
      } else {
        window.open(`https://wa.me/?text=${encodeURIComponent(note)}`, '_blank');
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') return;
    }
  };

  return (
    <DrawerMenu
      handleClose={handleClose}
      open={openPdfDrawer !== false}
      width="70%"
      closeButton
      flexColumn
      noPadding
      title={
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, maxWidth: '100%', minWidth: 0 }}>
          <Typography fontWeight={500} noWrap color="inherit" sx={{ minWidth: 0 }}>
            {headerText}
          </Typography>
          {downloadButtonText && (
            <>
              <Tooltip title={`${downloadButtonText} PDF`}>
                <Button
                  size="small"
                  variant="outlined"
                  aria-label={`${downloadButtonText} PDF`}
                  onClick={() => downloadContract(openPdfDrawer, 'PDF')}
                  sx={{
                    ...headerIconButtonSx,
                    minWidth: 0,
                    height: 26,
                    minHeight: 26,
                    px: 0.75,
                    py: 0,
                    fontSize: 11,
                    lineHeight: 1,
                    fontWeight: 600,
                    textTransform: 'none',
                  }}>
                  PDF
                </Button>
              </Tooltip>
              <Tooltip title={`${downloadButtonText} DOCX`}>
                <Button
                  size="small"
                  variant="outlined"
                  aria-label={`${downloadButtonText} DOCX`}
                  onClick={() => downloadContract(openPdfDrawer, 'DOCX')}
                  sx={{
                    ...headerIconButtonSx,
                    minWidth: 0,
                    height: 26,
                    minHeight: 26,
                    px: 0.75,
                    py: 0,
                    fontSize: 11,
                    lineHeight: 1,
                    fontWeight: 600,
                    textTransform: 'none',
                  }}>
                  DOCX
                </Button>
              </Tooltip>
            </>
          )}
          {prepareShareFile && (
            <Tooltip title={strings.bookings_button_share}>
              <IconButton
                size="small"
                color="primary"
                aria-label={strings.bookings_button_share}
                onClick={event => setShareAnchor(event.currentTarget)}
                sx={headerIconButtonSx}>
                <ShareOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      }>
      <Menu
        anchorEl={shareAnchor}
        open={Boolean(shareAnchor)}
        onClose={() => setShareAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ zIndex: theme => theme.zIndex.modal + 1 }}>
        <MenuItem onClick={() => handleShare('email')}>
          <ListItemIcon>
            <EmailOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>{strings.bookings_button_share_email}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleShare('whatsapp')}>
          <ListItemIcon>
            <WhatsAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{strings.bookings_button_share_whatsapp}</ListItemText>
        </MenuItem>
      </Menu>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', px: 3, bgcolor: 'grey.100' }}>
        <Box
          component="iframe"
          title={headerText}
          srcDoc={pdfUrl || ''}
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          sx={{
            flex: 1,
            width: '100%',
            height: '100%',
            minHeight: 0,
            border: 0,
            display: 'block',
            bgcolor: '#fff',
          }}
        />
      </Box>
    </DrawerMenu>
  );
};

export default PreviewContract;
